import _ from 'lodash';
import { getVisibleFailureReasons } from '@/accessControl/lockFailureReasons.utilities';
import { ChildRelationshipEnum } from '@seeqdev/api/model/LockErrorPathOutputV1';
import type { BaseOutput } from '@seeqdev/api/model/BaseOutput';
import type { LockErrorOutputV1 } from '@seeqdev/api/model/LockErrorOutputV1';
import type { LockErrorPathOutputV1 } from '@seeqdev/api/model/LockErrorPathOutputV1';

/**
 * The workbook reference each lock-error item's `item` carries, added by the backend in CRAB-63944
 * (`LockErrorWorkbookOutputV1` / `LockErrorItemOutputV1` in `LockErrorsOutputV1.kt`).
 *
 * This is defined locally rather than imported from the generated SDK, and that's permanent, not a stopgap: Swagger's
 * schema walker only emits a model for a class that's reachable as a *declared* type from some annotated endpoint.
 * `LockErrorItemOutputV1` is only ever assigned into a `BaseOutput`-typed field (`LockErrorOutputV1.item` /
 * `LockErrorPathOutputV1.item`), so it - and this `workbook` field on it - never gets discovered by codegen,
 * regenerating the SDK will never produce it. The real JSON response has the field regardless (confirmed against a
 * live lock-check response), since Jackson serializes the actual runtime object; only the generated types are blind
 * to it. `LockFailureReasons.atom.tsx` already does the same kind of local augmentation for a different lock-error
 * case (`LockErrorItemWithWorkbookAdded`), so this isn't a new pattern for this codebase.
 */
export type LockErrorWorkbookOutputV1 = {
  id: string;
  name: string;
  type: string; // e.g. 'Analysis' | 'Topic'
  locked: boolean;
};

/** `BaseOutput`, augmented with the not-yet-generated `workbook` field. */
export type LockErrorItemWithWorkbook = BaseOutput & { workbook?: LockErrorWorkbookOutputV1 };

/** `LockErrorOutputV1`, with `item` narrowed to carry the `workbook` field above. */
export type LockErrorOutputV1WithWorkbook = Omit<LockErrorOutputV1, 'item'> & { item: LockErrorItemWithWorkbook };

/**
 * Key used for the fallback group of lock errors with no resolvable
 * workbook - covers both globally-scoped items and Datasource-owned items,
 * per the design doc's resolved Open Question (no separate `reason`
 * distinction between the two for v1).
 */
export const GLOBAL_BUCKET_KEY = 'GLOBAL_ITEMS' as const;

export interface WorkbookLockErrorGroup {
  /** The grouping key: the workbook's id, or `GLOBAL_BUCKET_KEY` for the fallback group. */
  workbookId: string;
  /** The workbook itself, or `undefined` for the fallback group. */
  workbook: LockErrorWorkbookOutputV1 | undefined;
  errors: LockErrorOutputV1WithWorkbook[];
}

/**
 * Groups lock-blocking errors by their originating workbook,
 * so the Lock tab can render one collapsible section per workbook instead
 * of a single flat list.
 *
 * Items with no resolvable workbook are placed together in one trailing
 * fallback group (`GLOBAL_BUCKET_KEY`), regardless of *why* they have no
 * workbook (globally-scoped vs. Datasource-owned) - see the design doc's
 * resolved Open Question. Workbook groups otherwise preserve the order in
 * which their first item appears in `lockingErrors`; group/sort order
 * beyond that (alphabetical, current-workbook-first, etc.) was left as an
 * open decision in the original scoping doc and is not enforced here.
 *
 * @param lockingErrors - the flat list of lock errors, each with a `workbook`
 * reference already resolved by the backend
 * @returns one group per distinct workbook, plus a trailing fallback group
 * for items with no workbook
 */
export function groupLockErrorsByWorkbook(lockingErrors: LockErrorOutputV1WithWorkbook[]): WorkbookLockErrorGroup[] {
  // A Map keeps first-seen order, which is the group order we want (bar the fallback, moved last below).
  const grouped = new Map<string, LockErrorOutputV1WithWorkbook[]>();
  for (const error of lockingErrors) {
    const workbookId = error.item.workbook?.id ?? GLOBAL_BUCKET_KEY;
    grouped.set(workbookId, [...(grouped.get(workbookId) ?? []), error]);
  }

  const groups: WorkbookLockErrorGroup[] = [...grouped].map(([workbookId, errors]) => ({
    workbookId,
    workbook: errors[0].item.workbook,
    errors,
  }));

  // Keep the "Items outside these workbooks" fallback group last, regardless of where its items fell in the list.
  return [
    ...groups.filter((group) => group.workbookId !== GLOBAL_BUCKET_KEY),
    ...groups.filter((group) => group.workbookId === GLOBAL_BUCKET_KEY),
  ];
}

/**
 * Whether any of these groups came from a resolvable workbook.
 *
 * When this is false every error fell into the `GLOBAL_BUCKET_KEY` fallback, so there is nothing to group *by* - the
 * Lock tab renders its plain flat list instead of wrapping a single "Items outside these workbooks" section around
 * everything, which only added a header, a chevron and a count to a list that had not been grouped at all
 */
export function hasWorkbookGrouping(groups: WorkbookLockErrorGroup[]): boolean {
  return groups.some((group) => group.workbookId !== GLOBAL_BUCKET_KEY);
}

/**
 * Relationships for which the `GLOBAL_BUCKET_KEY` fallback group can state its reason once. A workbook group's summary
 * is always "lock this workbook first", but items with no workbook need copy specific to the relationship, so only
 * relationships that have that copy qualify (see `GROUPED_BY_WORKBOOK.OUTSIDE_WORKBOOKS_*` in en.json). Today that
 * is only the Vantage monitoring-source case; everything else keeps its per-item reason.
 */
export const GLOBAL_BUCKET_SUMMARIZABLE_RELATIONSHIPS: ReadonlyArray<ChildRelationshipEnum> = [
  ChildRelationshipEnum.MONITORINGSOURCENOTLOCKED,
];

/** The one failure reason that every error in a workbook group shares, as returned by `getGroupSharedReason`. */
export interface SharedGroupReason {
  /** The relationship every error in the group failed on. */
  childRelationship: NonNullable<LockErrorPathOutputV1['childRelationship']>;
  /** The reason's own item - the one that would be interpolated into the per-item reason text. */
  item: LockErrorPathOutputV1['item'];
}

/**
 * The failure reason shared by every error in a workbook group, if there is exactly one - which is what lets the Lock
 * tab state that reason once for the whole group instead of repeating it under every item.
 *
 * A group qualifies only when all of the following hold, so that collapsing can never hide information:
 *  - for a real workbook group, that workbook is itself unlocked, since the summary's whole point is "lock this
 *    workbook first"; a *locked* workbook that still blocks the lock is blocking for some other reason, which must
 *    stay visible, and
 *  - for the `GLOBAL_BUCKET_KEY` fallback (no workbook to lock), the shared relationship is one of
 *    `GLOBAL_BUCKET_SUMMARIZABLE_RELATIONSHIPS`, i.e. one that has settled copy for "no workbook", and
 *  - every error shows exactly one failure reason, and
 *  - every one of those reasons has the same `childRelationship` *and* the same reason item.
 *
 * The last condition is what makes this safe rather than merely tidy: same relationship plus same interpolated item
 * means every error renders a byte-identical sentence, so showing it once loses nothing. Two conditions blocked by
 * two *different* unlocked sources therefore fall back to per-item reasons rather than being summarized under one.
 * Collapsing is all-or-nothing per group; a partially uniform group renders per item.
 *
 * A group of one qualifies too. There is nothing to de-duplicate, but the summary layout (reason first, then the
 * items) is the same shape as its neighbours, whereas a lone per-item rendering (item first, then its reason) sat
 * visibly out of step with the groups around it.
 */
export function getGroupSharedReason(group: WorkbookLockErrorGroup): SharedGroupReason | undefined {
  const isGlobalBucket = group.workbookId === GLOBAL_BUCKET_KEY;
  if (!isGlobalBucket && (!group.workbook || group.workbook.locked)) {
    return undefined;
  }
  const soleReasons = _.map(group.errors, (error) => {
    const visibleReasons = getVisibleFailureReasons(error);
    return visibleReasons.length === 1 ? visibleReasons[0] : undefined;
  });
  const [firstReason] = soleReasons;
  if (!firstReason?.childRelationship) {
    return undefined;
  }

  const isShared = _.every(
    soleReasons,
    (reason) =>
      reason?.childRelationship === firstReason.childRelationship && reason?.item?.id === firstReason.item?.id,
  );

  if (!isShared) {
    return undefined;
  }
  if (isGlobalBucket && !GLOBAL_BUCKET_SUMMARIZABLE_RELATIONSHIPS.includes(firstReason.childRelationship)) {
    return undefined;
  }

  return { childRelationship: firstReason.childRelationship, item: firstReason.item };
}
